import React, { useRef, useEffect, useCallback, useState} from 'react';
import FormantVisualizer from './FormantVisualizer.js';
import DraggingFormantInfo from './DraggingFormantInfo.js';
import FormantParameters from './FormantParameters.js';
import Formant from './Formant.js';
import {connect} from 'react-redux';
import {FORMANTS_WIDTH, FORMANTS_HEIGHT} from './constants.js';
import {
    setEffectParameterValues,
    setShowingFormants} from 'actions/index.js';

const FormantsEditor = ({
    oscillator,
    selectedParameter,
    setShowingFormants,
    setEffectParameterValues
}) => { 
    let ref = useRef(undefined);
    let {formants} = oscillator;
    let [draggingFormant, setDraggingFormant] = useState(undefined);
    let [selectedFormant, setSelectedFormant] = useState(undefined);

    useEffect(() => {
        setShowingFormants(true);
        return () => setShowingFormants(false);
    }, []);

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [setSelectedFormant, selectedFormant, onKeyDown]);

    useEffect(() => {
        ref.current.addEventListener("mousewheel", onWheel, {passive: false});
        return () => ref.current.removeEventListener("mousewheel", onWheel);
    }, [selectedFormant]);

    const onWheel = useCallback((e) => {
        e.preventDefault();
        if (!selectedFormant) { return; }
        let {deltaX, deltaY, wheelDeltaX, wheelDeltaY} = e;
        let bulk = {};
        if (Math.abs(deltaX) > 5) {
            // frequency
            let {id, value} = selectedFormant.formantFrequencyParameter.moveCoordinates(
                deltaX / -10, 0);
            bulk[id] = value;
        }
        if (Math.abs(deltaY) > 5) {
            // gain
            let {id, value} = selectedFormant.formantGainParameter.moveCoordinates(
                deltaY / 10, 0);
            bulk[id] = value;
        } else if (Math.abs(wheelDeltaY) > 4) {
            // q
            let {id, value} = selectedFormant.formantQParameter.moveCoordinates(
                wheelDeltaY / -28, 0);
            bulk[id] = value;
        }
        if (Object.keys(bulk).length > 0) {
            setEffectParameterValues(bulk);
        }
    }, [selectedFormant]);

    const onKeyDown = useCallback((e) => {
        if (e.key === 'Tab' && selectedFormant) {
            // find the list of enabled formants
            let enabled = formants.filter(x => x.enabledParameter.get());
            let i = enabled.indexOf(selectedFormant);
            setSelectedFormant(enabled[(i+1)%enabled.length]);
        }
    }, [setSelectedFormant, selectedFormant]);


    useEffect(() => {
        if (draggingFormant && selectedFormant !== draggingFormant) {
            setSelectedFormant(draggingFormant);
        }
    }, [draggingFormant, setSelectedFormant]);

    useEffect(() => {
        setSelectedFormant(undefined);
    }, [setSelectedFormant, oscillator]);

    useEffect(() => {
        if (selectedParameter && selectedParameter.context) {
            if (selectedParameter.context.formant) {
                setSelectedFormant(selectedParameter.context.formant);
            }
        }
    }, [selectedParameter, setSelectedFormant]);

    const onMouseMove = useCallback((e) => {
        if (!draggingFormant) {
            return;
        }

        let rect = ref.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        let freq = Math.pow(x / FORMANTS_WIDTH, 2) * 20000;
        let gain = draggingFormant.formantGainParameter.originalMax * (FORMANTS_HEIGHT - y) / FORMANTS_HEIGHT;
        draggingFormant.formantFrequencyParameter.set(freq, false);
        draggingFormant.formantGainParameter.set(gain, false);
    }, [draggingFormant]);

    return (
        <div
          className="formants-editor">
          <div
            ref={ref}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={true}
            onDragStart={(e) => e.preventDefault()}
            onMouseMove={onMouseMove}
            className="formants-wrapper">
            {formants.map(
                (formant, i) =>
                    <FormantVisualizer
                      key={i}
                      isSelected={selectedFormant === formant}
                      setDraggingFormant={setDraggingFormant}
                      sequencerEngine={oscillator.track.sequencerEngine}
                      width={FORMANTS_WIDTH}
                      height={FORMANTS_HEIGHT}
                      formant={formant}/>)}
            {formants.map(
                (formant, i) =>
                    <Formant
                      key={i}
                      isSelected={selectedFormant === formant}
                      setDraggingFormant={setDraggingFormant}
                      width={FORMANTS_WIDTH}
                      height={FORMANTS_HEIGHT}
                      formant={formant}/>)}
          </div>
        {selectedFormant && !draggingFormant ? <FormantParameters formant={selectedFormant}/> :
         draggingFormant ? <DraggingFormantInfo formant={draggingFormant}/> : ''}
        </div>);
}

const mapStateToProps = (state, props) => {
  return {
        selectedParameter: state.effects.accumulatorParameterSelected
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setShowingFormants: (x) => dispatch(setShowingFormants(x)),
      setEffectParameterValues: (x) => dispatch(setEffectParameterValues(x))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormantsEditor);
